import { memo, useState, useEffect, type ComponentProps } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button from "common/core/button";
import { DeprecatedSelectInput } from "common/form/inputs/select";
import { encodeSearchParams } from "util/location";
import { useMutation } from "util/graphql";
import Env from "config/environment";
import { isNotaryODN, isNotaryODNOptedIn } from "common/notary/capacity";
import { Paragraph } from "common/core/typography";

import type {
  NotaryProfileWizardPayout as User,
  NotaryProfileWizardPayout_notaryProfile as NotaryProfile,
} from "./index_fragment.graphql";
import ConnectStripeExpressWithNotaryProfileMutation from "./connect_stripe_express_with_notary_profile_mutation.graphql";
import WarnNavigateOffNotarizeModal from "./warn_navigate_off_notarize";
import type PaymentSection from ".";
import Styles from "./connect_to_stripe.module.scss";

const { connectToStripeClientId } = Env;
const STRIPE_CONNECT_EXPRESS_URL = "https://connect.stripe.com/express/oauth/authorize";
const STRIPE_CONNECT_ACCOUNT_TYPES = [
  { value: "individual", label: "Individual Account" },
  { value: "company", label: "Company Account" },
];

function buildStripeConnectURL(user: User, accountType: string) {
  const { firstName, lastName, email, organization } = user;
  const url_params = encodeSearchParams(new URLSearchParams(), {
    client_id: connectToStripeClientId,
    redirect_uri: window.location.href, // this value must be in the stripe connect redirect whitelist in order to complete the redirect
    "stripe_user[email]": email,
    "stripe_user[first_name]": firstName,
    "stripe_user[last_name]": lastName,
    "stripe_user[business_name]": organization?.name,
    "stripe_user[business_type]": accountType,
    "stripe_user[product_description]": "Notarial services provided using Proof.com software.",
  });
  return `${STRIPE_CONNECT_EXPRESS_URL}?${url_params}`;
}

function getStripeText(notaryProfile: NotaryProfile) {
  const isOdn = isNotaryODN(notaryProfile);
  const isOptedIn = isNotaryODNOptedIn(notaryProfile);

  if (isOdn) {
    return (
      <>
        <Paragraph>
          {isOptedIn ? (
            <FormattedMessage
              id="1c0e1e16-0bb5-4d78-98ca-0923960b73d2"
              defaultMessage="For transactions notarized through the Notarize Network, Proof partners with Stripe Connect for fast and secure payments. Your Notarize Network funds will be deposited directly into your Stripe payout account."
            />
          ) : (
            <FormattedMessage
              id="30ed0581-90f4-4949-8ffd-919edb8514d5"
              defaultMessage="For transactions notarized through the Notarize Network, Proof partners with Stripe Connect for fast and secure payments. If you decide to later join the Notarize Network, your funds will be deposited directly into your Stripe payout account."
            />
          )}
        </Paragraph>
        <Paragraph>
          <FormattedMessage
            id="3ad19e5c-d52a-4676-bdcb-151ebc948c2f"
            defaultMessage="You may setup your Stripe Connect account as an individual account (SSN is required) or as a company account (EIN or SSN need to be provided)."
          />
        </Paragraph>
      </>
    );
  }
  return (
    <>
      <Paragraph>
        <FormattedMessage
          id="baf33f17-782d-4559-a0d6-b799952bd573"
          defaultMessage="Proof partners with Stripe Connect for fast and secure payments. Notaries are required to setup a payout account, but you can always choose to bill signers externally. You may setup your Stripe Connect account as an individual account (SSN is required) or as a company account (EIN or SSN need to be provided)."
        />
      </Paragraph>
      <Paragraph>
        <FormattedMessage
          id="4214bc3a-3266-487a-b8e6-36033023bf16"
          defaultMessage="When signers pay through Proof, funds are deposited directly into your Stripe payout account."
        />
      </Paragraph>
    </>
  );
}

function ConnectToStripe({ user }: ComponentProps<typeof PaymentSection>) {
  const navigate = useNavigate();
  const notaryProfile = user.notaryProfile!;
  const { stripeConnected } = notaryProfile;
  const [connecting, setConnecting] = useState(false);
  const [codeUsed, setCodeUsed] = useState(false);
  const [showWarnNavigateOffNotarizeModal, setShowWarnNavigateOffNotarizeModal] = useState(false);
  const [stripeConnectAccountType, setStripeConnectAccountType] = useState("individual");

  const connectStripeExpressWithNotaryProfileMutateFn = useMutation(
    ConnectStripeExpressWithNotaryProfileMutation,
  );

  const renderWarnNavigateOffNotarizeModal = ({ redirectUrl }: { redirectUrl: string }) => {
    const cancel = () => {
      setShowWarnNavigateOffNotarizeModal(false);
    };
    return (
      <WarnNavigateOffNotarizeModal
        onContinue={() => {
          window.open(redirectUrl);
          cancel();
        }}
        onCancel={cancel}
      />
    );
  };

  useEffect(() => {
    if (connecting || stripeConnected || codeUsed) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) {
      return;
    }
    setConnecting(true);
    setCodeUsed(true);
    connectStripeExpressWithNotaryProfileMutateFn({
      variables: {
        input: {
          id: user.notaryProfile!.id,
          code,
        },
      },
    })
      .then(() => {
        navigate(window.location.pathname);
      })
      .finally(() => {
        setConnecting(false);
      });
  }, [connecting, navigate, stripeConnected, codeUsed]);

  return (
    <>
      {!stripeConnected && getStripeText(notaryProfile)}
      {!stripeConnected &&
        showWarnNavigateOffNotarizeModal &&
        renderWarnNavigateOffNotarizeModal({
          redirectUrl: buildStripeConnectURL(user, stripeConnectAccountType),
        })}
      {!stripeConnected && (
        <div>
          <div className={Styles.accountTypeLabel}>
            <FormattedMessage
              id="9a57207c-3dfd-47de-9271-71014591fdc6"
              defaultMessage="Account Type"
            />
          </div>
          <div className={Styles.accountSection}>
            <DeprecatedSelectInput
              items={STRIPE_CONNECT_ACCOUNT_TYPES}
              onChange={(value: string) => setStripeConnectAccountType(value)}
              value={stripeConnectAccountType}
              className={Styles.accountType}
            />
            <Button
              buttonColor="action"
              variant="primary"
              onClick={() => setShowWarnNavigateOffNotarizeModal(true)}
              className={Styles.connectStripe}
            >
              <FormattedMessage
                id="de26cd6f-6012-47b0-bc27-74141adb4652"
                defaultMessage="Connect to Stripe"
              />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

export default memo(ConnectToStripe);
